import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  CircularProgress,
  Box,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import PublishIcon from '@material-ui/icons/Publish';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import PersonIcon from '@material-ui/icons/Person';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import AssessmentIcon from '@material-ui/icons/Assessment';
import GroupIcon from '@material-ui/icons/Group';
import CreateIcon from '@material-ui/icons/Create';
import HistoryIcon from '@material-ui/icons/History';
import CommentIcon from '@material-ui/icons/Comment';
/****/
const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #e0e0e0',
    padding: '12px 24px',
  },
  titleText: {
    fontFamily: 'VodafoneRg-bold',
    fontSize: '1.2rem',
    color: '#333',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  dialogContent: {
    padding: '16px 24px',
    minWidth: '480px',
    maxHeight: '500px',
    overflowY: 'auto',
  },
  timelineContainer: {
    position: 'relative',
    paddingLeft: '40px',
  },
  timelineLine: {
    position: 'absolute',
    left: '18px',
    top: '0',
    bottom: '0',
    width: '2px',
    backgroundColor: '#e0e0e0',
  },
  timelineEntry: {
    position: 'relative',
    marginBottom: '20px',
    paddingBottom: '4px',
  },
  timelineIcon: {
    position: 'absolute',
    left: '-40px',
    top: '0',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    zIndex: 1,
  },
  actionTitle: {
    fontFamily: 'VodafoneRg-bold',
    fontSize: '0.95rem',
    color: '#333',
    marginBottom: '2px',
  },
  actionMeta: {
    fontSize: '0.8rem',
    color: '#888',
    marginBottom: '2px',
  },
  actionDetails: {
    fontSize: '0.85rem',
    color: '#666',
    fontStyle: 'italic',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#999',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px',
  },
  changeValues: {
    fontSize: '0.85rem',
    color: '#555',
  },
  approveComment: {
    fontSize: '0.85rem',
    color: '#1565c0',
    backgroundColor: '#e3f2fd',
    borderLeft: '3px solid #1976d2',
    padding: '6px 10px',
    marginTop: '6px',
    borderRadius: '4px',
    fontStyle: 'italic',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
  },
}));

const ACTION_CONFIG = {
  CREATED: { label: 'Idea Created', icon: CreateIcon, color: '#757575' },
  EDITED: { label: 'Idea Edited', icon: EditIcon, color: '#1976d2' },
  PUBLISHED: { label: 'Idea Published', icon: PublishIcon, color: '#7b1fa2' },
  STATUS_CHANGED: { label: 'Status Changed', icon: HistoryIcon, color: '#f57c00' },
  EVALUATOR_ASSIGNED: { label: 'Evaluator Assigned', icon: PersonAddIcon, color: '#3949ab' },
  EVALUATOR_REMOVED: { label: 'Evaluator Removed', icon: RemoveCircleIcon, color: '#c62828' },
  RATING_SAVED: { label: 'Rating Saved', icon: StarBorderIcon, color: '#ef6c00' },
  RATING_SUBMITTED: { label: 'Rating Submitted', icon: StarIcon, color: '#2e7d32' },
  TEAM_MEMBER_ADDED: { label: 'Team Member Added', icon: PersonAddIcon, color: '#00695c' },
  TEAM_MEMBER_REMOVED: { label: 'Team Member Removed', icon: RemoveCircleIcon, color: '#d32f2f' },
  APPROVED: { label: 'Idea Approved', icon: CheckCircleIcon, color: '#2e7d32' },
  BUSINESS_SCORECARD_ADDED: { label: 'Business Scorecard Added', icon: AssessmentIcon, color: '#1565c0' },
  COMMUNITY_EVALUATOR_ASSIGNED: { label: 'Community Evaluator Assigned', icon: GroupIcon, color: '#6a1b9a' },
  MOVED_TO_DRAFT: { label: 'Moved to Draft', icon: HistoryIcon, color: '#ff8f00' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return dateStr;
  }
};

/** Extract approve comment: prefer explicit field, fallback to parsing details */
const getApproveComment = (entry) => {
  if (entry.approveComment) return entry.approveComment;
  if (entry.details && entry.details.includes(' with comment: ')) {
    return entry.details.split(' with comment: ').slice(1).join(' with comment: ');
  }
  return null;
};

const IdeaHistory = ({ open, onClose, historyData, isLoading }) => {
  const classes = useStyles();
  const historyList = Array.isArray(historyData) ? historyData : [];

  // Filter out RATING_SAVED entries – only the final submitted rating matters
  const filteredHistory = historyList.filter((entry) => entry.action !== 'RATING_SAVED');

  // Group consecutive EDITED entries by same user within 60 seconds into one block
  const groupedHistory = [];
  filteredHistory.forEach((entry) => {
    if (entry.action === 'EDITED' && groupedHistory.length > 0) {
      const lastGroup = groupedHistory[groupedHistory.length - 1];
      if (
        lastGroup.action === 'EDITED' &&
        lastGroup.actionBy === entry.actionBy &&
        Math.abs(new Date(lastGroup.actionDate).getTime() - new Date(entry.actionDate).getTime()) < 60000
      ) {
        if (!lastGroup._groupedFields) {
          lastGroup._groupedFields = [{ fieldChanged: lastGroup.fieldChanged, oldValue: lastGroup.oldValue, newValue: lastGroup.newValue, details: lastGroup.details }];
        }
        lastGroup._groupedFields.push({ fieldChanged: entry.fieldChanged, oldValue: entry.oldValue, newValue: entry.newValue, details: entry.details });
        return;
      }
    }
    groupedHistory.push({ ...entry });
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <div className={classes.dialogTitle}>
        <Typography className={classes.titleText}>
          <HistoryIcon /> Idea History
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent className={classes.dialogContent}>
        {isLoading && (
          <div className={classes.loadingContainer}>
            <CircularProgress size={32} />
          </div>
        )}
        {!isLoading && groupedHistory.length === 0 && (
          <div className={classes.emptyState}>
            <HistoryIcon style={{ fontSize: 48, marginBottom: 12, opacity: 0.4 }} />
            <Typography>No history available for this idea yet.</Typography>
          </div>
        )}
        {!isLoading && groupedHistory.length > 0 && (
          <div className={classes.timelineContainer}>
            <div className={classes.timelineLine} />
            {groupedHistory.map((entry) => {
              const config = ACTION_CONFIG[entry.action] || {
                label: entry.action,
                icon: HistoryIcon,
                color: '#999',
              };
              const IconComp = config.icon;
              const fields = entry._groupedFields || [{ fieldChanged: entry.fieldChanged, oldValue: entry.oldValue, newValue: entry.newValue, details: entry.details }];
              const entryKey = entry.id || [
                entry.action,
                entry.actionDate,
                entry.actionBy,
                entry.actionByName,
                entry.fieldChanged,
                entry.oldValue,
                entry.newValue,
              ].filter(Boolean).join('|');

              return (
                <div key={entryKey} className={classes.timelineEntry}>
                  <div
                    className={classes.timelineIcon}
                    style={{ backgroundColor: config.color }}
                  >
                    <IconComp style={{ fontSize: 18 }} />
                  </div>
                  <Typography className={classes.actionTitle}>
                    {(entry.action === 'STATUS_CHANGED' || entry.action === 'APPROVED') && entry.newValue
                      ? `Status Changed to ${entry.newValue.replace(/_/g, ' ')}`
                      : config.label}
                  </Typography>
                  {fields.map((field) => {
                    const fieldKey = [
                      field.fieldChanged,
                      field.oldValue,
                      field.newValue,
                      field.details,
                    ].filter(value => value !== undefined && value !== null).join('|');

                    return (
                    <Box key={fieldKey} style={{ marginBottom: 4 }}>
                      {field.fieldChanged && entry.action !== 'EVALUATOR_REMOVED' && (
                        <Typography className={classes.changeValues} style={{ fontWeight: 600 }}>
                          {field.fieldChanged.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}:
                        </Typography>
                      )}
                      {field.details && entry.action === 'EVALUATOR_REMOVED' && (
                        <Typography className={classes.changeValues}>
                          {field.details}
                        </Typography>
                      )}
                      {entry.action !== 'EVALUATOR_REMOVED' && (field.oldValue || field.newValue) && (
                        <Typography className={classes.changeValues}>
                          {(() => {
                            if (entry.action === 'RATING_SUBMITTED') {
                              return field.newValue || field.oldValue;
                            }
                            if (field.oldValue && field.newValue) {
                              return `${field.oldValue} → ${field.newValue}`;
                            }
                            return field.newValue || field.oldValue;
                          })()}
                        </Typography>
                      )}
                      {field.details && !field.oldValue && !field.newValue && entry.action !== 'EVALUATOR_REMOVED' && (
                        <Typography className={classes.changeValues}>
                          {field.details}
                        </Typography>
                      )}
                    </Box>
                    );
                  })}
                  <Typography className={classes.actionMeta}>
                    By <strong>{entry.actionByName || entry.actionBy || 'System'}</strong>
                    {entry.editedByRole ? ` (${entry.editedByRole})` : ''}
                    {entry.actionDate ? ` · ${formatDate(entry.actionDate)}` : ''}
                  </Typography>
                  {entry.ideaStatusAtEdit && (
                    <Typography className={classes.actionMeta}>
                      Status at edit: <strong>{entry.ideaStatusAtEdit}</strong>
                    </Typography>
                  )}
                  {(entry.action === 'STATUS_CHANGED' || entry.action === 'APPROVED') && getApproveComment(entry) && (
                    <div className={classes.approveComment}>
                      <CommentIcon style={{ fontSize: 16, marginTop: 1, flexShrink: 0 }} />
                      <span><strong>Comment:</strong> {getApproveComment(entry)}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default IdeaHistory;

